/**
 * サブスクリプション解約確認ダイアログ
 * @module components/subscription/CancellationConfirmDialog
 */

import { Alert, Checkbox, Typography, Space } from 'antd';
import { useState } from 'react';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import type { Company } from '../../types/company';
import { withDevModalFilePath } from '../common';

const { Text, Title } = Typography;
const { Modal } = withDevModalFilePath('client/src/components/subscription/CancellationConfirmDialog.tsx');

interface CancellationConfirmDialogProps {
  open: boolean;
  company: Company;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

/**
 * 日付フォーマット
 */
const formatDate = (date: Date | string | null | undefined): string => {
  if (!date) return '-';
  return format(new Date(date), 'yyyy年MM月dd日', { locale: ja });
};

/**
 * サブスクリプション解約確認ダイアログ
 */
export function CancellationConfirmDialog({
  open,
  company,
  onConfirm,
  onCancel,
  loading = false,
}: CancellationConfirmDialogProps) {
  const [agreed, setAgreed] = useState(false);

  const cancelEffectiveDate = company.subscriptionEndsAt;

  const handleConfirm = () => {
    if (!agreed) {
      return;
    }
    onConfirm();
    setAgreed(false);
  };

  const handleCancel = () => {
    setAgreed(false);
    onCancel();
  };

  return (
    <Modal
      title={
        <Title level={4} style={{ margin: 0 }}>
          サブスクリプションの解約
        </Title>
      }
      open={open}
      onOk={handleConfirm}
      onCancel={handleCancel}
      okText="解約する"
      okType="danger"
      cancelText="キャンセル"
      okButtonProps={{ disabled: !agreed, loading }}
      width={600}
      destroyOnHidden
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Alert
          type="warning"
          message="重要な確認事項"
          description={
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text>解約を実行すると、以下の影響があります：</Text>

              <div style={{ paddingLeft: 16 }}>
                <Space direction="vertical" size="middle">
                  <div>
                    <Text strong>■ 契約終了日</Text>
                    <br />
                    <Text>{formatDate(cancelEffectiveDate)}</Text>
                    <br />
                    <Text type="secondary">
                      それまでは通常通りご利用いただけます
                    </Text>
                  </div>

                  <div>
                    <Text strong>■ 契約終了後</Text>
                    <br />
                    <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                      <li>ログインできなくなります</li>
                      <li>データは削除されます</li>
                      <li>削除されたデータは復元できません</li>
                    </ul>
                  </div>

                  <div>
                    <Text strong>■ 再度ご利用いただく場合</Text>
                    <br />
                    <Text>
                      新規登録が必要になります。
                    </Text>
                  </div>
                </Space>
              </div>
            </Space>
          }
        />

        <Checkbox
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
        >
          <Text strong>上記の内容を理解し、解約に同意します</Text>
        </Checkbox>
      </Space>
    </Modal>
  );
}
